import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Reusable Image Sequence Function
 * @param {Object} options 
 * @param {number} options.scrollDuration - The total scroll distance in pixels (default: 4000)
 */
const setupImageSequence = ({ 
  canvasId, 
  pathBuilder, 
  frameCount, 
  triggerSelector, 
  textSelector, 
  scrollDuration = 4000 
}) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  const context = canvas.getContext("2d");
  const images = [];
  const sequenceState = { frame: 0 };

  // 1. New Resize Function
  function resizeCanvas() {
    // Set internal resolution to match the actual display size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    render(); 
  }

  // 2. Optimized Render Function
  function render() {
    context.clearRect(0, 0, canvas.width, canvas.height);
    const img = images[sequenceState.frame];
    
    if (img && img.complete) {
      // Calculate "Aspect Fill" (Cover) logic
      const canvasAspect = canvas.width / canvas.height;
      const imgAspect = img.width / img.height;
      let drawWidth, drawHeight, offsetX, offsetY;

      if (canvasAspect > imgAspect) {
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgAspect;
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) / 2;
      } else {
        drawWidth = canvas.height * imgAspect;
        drawHeight = canvas.height;
        offsetX = (canvas.width - drawWidth) / 2;
        offsetY = 0;
      }

      context.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    }
  }

  // Initial setup
  window.addEventListener('resize', resizeCanvas);
  resizeCanvas();

  // ... rest of your image loading loop ...
  for (let i = 0; i < frameCount; i++) {
    const img = new Image();
    img.src = pathBuilder(i);
    // Ensure the first frame renders once it loads
    if (i === 0) img.onload = render; 
    images.push(img);
  }

  // GSAP Timeline (Keep as is, but ensure onUpdate calls render)
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: triggerSelector,
      start: "top top",
      end: `+=${scrollDuration}`,
      scrub: 1,
      pin: true,
      invalidateOnRefresh: true // Important for resizes!
    }
  });

  tl.to(sequenceState, {
    frame: frameCount - 1,
    snap: "frame",
    ease: "none",
    onUpdate: render,
    duration: 1
  });

  if (textSelector) {
    tl.to(textSelector, {
      y: -100,
      opacity: 1,
      duration: 0.5
    }, "-=0.3");
  }

  tl.to({}, { duration: 0.15 });
};

// --- INITIALIZE SEQUENCES ---

// 1. Home Page (116 frames - longer scroll)
setupImageSequence({
  canvasId: "hero-lightpass",
  frameCount: 116,
  pathBuilder: (index) => `/assets/seq/bmv_${index.toString().padStart(3, '0')}.webp`,
  triggerSelector: ".scroll-container",
  textSelector: ".scroll-text",
  scrollDuration: 4000 
});

// 2. About Us (39 frames - shorter scroll)
setupImageSequence({
  canvasId: "about-canvas",
  frameCount: 39, 
  pathBuilder: (index) => `/assets/seq/about_${index.toString().padStart(3, '0')}.webp`,
  triggerSelector: ".about-scroll-container",
  textSelector: ".about-scroll-text",
  scrollDuration: 1500 // Reduced this since you have fewer frames
});

// Dynamic Cart Management Logic
const cart = [];
const cartSummary = document.getElementById('cart-summary');
const cartItemsContainer = document.getElementById('cart-items');
const cartTotalElement = document.getElementById('cart-total');

document.querySelectorAll('.add-to-cart-dynamic').forEach(button => {
  button.addEventListener('click', () => {
    const selectId = button.getAttribute('data-id');
    const baseName = button.getAttribute('data-base-name');
    const selectElement = document.getElementById(selectId);
    
    // Get selected option data
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const price = parseFloat(selectedOption.value);
    const label = selectedOption.getAttribute('data-label');
    const fullName = `${baseName} (${label})`;

    // Add to local state
    cart.push({ name: fullName, price: price });
    updateCartUI();
    
    // Feedback Animation
    gsap.fromTo(button, { scale: 0.95 }, { scale: 1, duration: 0.2, ease: "back.out(2)" });
  });
});

function updateCartUI() {
  if (cart.length > 0) {
    cartSummary.classList.remove('d-none');
    gsap.fromTo(cartSummary, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3 });
  }

  cartItemsContainer.innerHTML = cart.map((item, index) => `
    <div class="d-flex justify-content-between mb-2 border-bottom pb-1">
      <span class="text-truncate" style="max-width: 180px;">${item.name}</span>
      <span class="fw-bold">RM ${item.price}</span>
    </div>
  `).join('');

  const total = cart.reduce((sum, item) => sum + item.price, 0);
  cartTotalElement.innerText = `RM ${total}`;
}